import { useCallback, useEffect, useMemo, useState } from 'react';
import type { CSSProperties, FormEvent } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { apiDelete, apiGet, apiPatch, apiPost, TENANT_TOKEN_KEY } from '../../api/client';
import { useAgentSelector } from '../../auth/AgentSelectorContext';

type TriggerRow = {
  id: string;
  title: string | null;
  phrases: string[];
  matchMode: string;
  reason: string;
  replyMessage: string | null;
  sortOrder: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
};

type AgentBlock = {
  id: string;
  name: string;
  model: string;
  isActive: boolean;
  triggerCount: number;
  activeTriggerCount: number;
  triggers: TriggerRow[];
};

type Payload = {
  maxActivePerAgent: number;
  agents: AgentBlock[];
};

const TITLE_MAX = 120;
const REPLY_MAX = 2000;

const REASON_LABELS: Record<string, string> = {
  explicit: 'Pedido de humano',
  demo_request: 'Solicitud de demo',
};

const MATCH_LABELS: Record<string, string> = {
  contains: 'Contiene',
  exact: 'Exacto',
};

function clip(s: string, n: number): string {
  const t = s.replace(/\s+/g, ' ').trim();
  if (t.length <= n) return t;
  return `${t.slice(0, n - 1)}…`;
}

function phrasesToText(phrases: string[]): string {
  return phrases.join('\n');
}

function textToPhrases(text: string): string[] {
  return text
    .split(/\r?\n/)
    .map((s) => s.trim())
    .filter(Boolean);
}

export function HandoffTriggersPage() {
  const token = localStorage.getItem(TENANT_TOKEN_KEY)!;
  const { selectedAgentId } = useAgentSelector();
  const [data, setData] = useState<Payload | null>(null);
  const [err, setErr] = useState('');
  const [search, setSearch] = useState('');
  const [helpOpen, setHelpOpen] = useState(true);
  const [sectionOpen, setSectionOpen] = useState<Record<string, boolean>>({});

  const [addAgentId, setAddAgentId] = useState<string | null>(null);
  const [nTitle, setNTitle] = useState('');
  const [nPhrases, setNPhrases] = useState('');
  const [nMatchMode, setNMatchMode] = useState<'contains' | 'exact'>('contains');
  const [nReason, setNReason] = useState<'explicit' | 'demo_request'>('demo_request');
  const [nReply, setNReply] = useState('');
  const [nBusy, setNBusy] = useState(false);

  const [editing, setEditing] = useState<{ agentId: string; trigger: TriggerRow } | null>(null);
  const [eTitle, setETitle] = useState('');
  const [ePhrases, setEPhrases] = useState('');
  const [eMatchMode, setEMatchMode] = useState<'contains' | 'exact'>('contains');
  const [eReason, setEReason] = useState<'explicit' | 'demo_request'>('explicit');
  const [eReply, setEReply] = useState('');
  const [eActive, setEActive] = useState(true);
  const [eBusy, setEBusy] = useState(false);

  const [deleteModal, setDeleteModal] = useState<{ agentId: string; trigger: TriggerRow } | null>(null);
  const [deleteBusy, setDeleteBusy] = useState(false);

  const load = useCallback(async () => {
    setErr('');
    try {
      const d = await apiGet<Payload>('/api/console/handoff-triggers', token);
      setData(d);
    } catch (e) {
      setErr((e as Error).message);
    }
  }, [token]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (!editing && !deleteModal) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !eBusy && !deleteBusy) {
        setEditing(null);
        setDeleteModal(null);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [editing, deleteModal, eBusy, deleteBusy]);

  const visibleAgents = useMemo(() => {
    if (!data) return [];
    const q = search.trim().toLowerCase();
    return data.agents.filter((a) => {
      if (selectedAgentId && a.id !== selectedAgentId) return false;
      if (!q) return true;
      if (a.name.toLowerCase().includes(q)) return true;
      return a.triggers.some(
        (t) =>
          (t.title?.toLowerCase().includes(q) ?? false) ||
          t.phrases.some((p) => p.toLowerCase().includes(q)) ||
          t.reason.toLowerCase().includes(q),
      );
    });
  }, [data, search, selectedAgentId]);

  const maxActive = data?.maxActivePerAgent ?? 20;

  const toggleSection = (agentId: string) => {
    setSectionOpen((s) => ({ ...s, [agentId]: !(s[agentId] ?? true) }));
  };

  const startAdd = (agentId: string) => {
    setSectionOpen((s) => ({ ...s, [agentId]: true }));
    setAddAgentId((x) => (x === agentId ? null : agentId));
    setNTitle('');
    setNPhrases('');
    setNMatchMode('contains');
    setNReason('demo_request');
    setNReply('');
  };

  const submitAdd = async (e: FormEvent, agentId: string) => {
    e.preventDefault();
    const phrases = textToPhrases(nPhrases);
    if (phrases.length === 0) {
      setErr('Agregá al menos una frase (una por línea).');
      return;
    }
    setNBusy(true);
    setErr('');
    try {
      await apiPost(
        `/api/console/agents/${agentId}/handoff-triggers`,
        {
          title: nTitle.trim() || undefined,
          phrases,
          matchMode: nMatchMode,
          reason: nReason,
          replyMessage: nReply.trim() || null,
        },
        token,
        { successMessage: 'Trigger agregado' },
      );
      setAddAgentId(null);
      void load();
    } catch (ex) {
      setErr((ex as Error).message);
    } finally {
      setNBusy(false);
    }
  };

  const openEdit = (agentId: string, trigger: TriggerRow) => {
    setSectionOpen((s) => ({ ...s, [agentId]: true }));
    setEditing({ agentId, trigger });
    setETitle(trigger.title ?? '');
    setEPhrases(phrasesToText(trigger.phrases));
    setEMatchMode(trigger.matchMode === 'exact' ? 'exact' : 'contains');
    setEReason(trigger.reason === 'demo_request' ? 'demo_request' : 'explicit');
    setEReply(trigger.replyMessage ?? '');
    setEActive(trigger.isActive);
  };

  const saveEdit = async (e: FormEvent) => {
    e.preventDefault();
    if (!editing) return;
    const phrases = textToPhrases(ePhrases);
    if (phrases.length === 0) {
      setErr('Agregá al menos una frase.');
      return;
    }
    setEBusy(true);
    setErr('');
    try {
      await apiPatch(
        `/api/console/agents/${editing.agentId}/handoff-triggers/${editing.trigger.id}`,
        {
          title: eTitle.trim() || null,
          phrases,
          matchMode: eMatchMode,
          reason: eReason,
          replyMessage: eReply.trim() || null,
          isActive: eActive,
        },
        token,
        { successMessage: 'Trigger actualizado' },
      );
      setEditing(null);
      void load();
    } catch (ex) {
      setErr((ex as Error).message);
    } finally {
      setEBusy(false);
    }
  };

  const toggleActive = async (agentId: string, trigger: TriggerRow) => {
    setErr('');
    try {
      await apiPatch(
        `/api/console/agents/${agentId}/handoff-triggers/${trigger.id}`,
        { isActive: !trigger.isActive },
        token,
        { successMessage: trigger.isActive ? 'Trigger desactivado' : 'Trigger activado' },
      );
      void load();
    } catch (ex) {
      setErr((ex as Error).message);
    }
  };

  const confirmDelete = async () => {
    if (!deleteModal) return;
    setDeleteBusy(true);
    setErr('');
    try {
      await apiDelete(
        `/api/console/agents/${deleteModal.agentId}/handoff-triggers/${deleteModal.trigger.id}`,
        token,
        { successMessage: 'Trigger eliminado' },
      );
      setDeleteModal(null);
      void load();
    } catch (ex) {
      setErr((ex as Error).message);
    } finally {
      setDeleteBusy(false);
    }
  };

  return (
    <>
      <div style={pageShell}>
        <div style={pageScroll}>
          <div style={pageHeaderRow}>
            <div>
              <h1 style={pageTitle}>Handoff a humano</h1>
              <p style={pageSubtitle}>
                Frases que pasan la conversación a modo humano y disparan la alerta según el motivo.
              </p>
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
              <button type="button" onClick={() => void load()} style={btnGhost}>
                ⟳ Actualizar
              </button>
              <button type="button" onClick={() => setHelpOpen((o) => !o)} style={btnGhost}>
                {helpOpen ? 'Ocultar ayuda' : 'Cómo funciona'}
              </button>
            </div>
          </div>

          {helpOpen ? (
            <div style={helpBox}>
              <p style={{ margin: 0, fontSize: 13, color: '#334155', lineHeight: 1.65, maxWidth: 920 }}>
                Si el mensaje del visitante <strong>contiene</strong> (o coincide exacto con) alguna frase, se responde
                con el mensaje configurado, se pasa a modo humano y se emite la alerta de atención humana con el motivo
                elegido. El filtro de quién recibe la alerta se configura en{' '}
                <Link to="/app/settings/notifications" style={{ color: '#0369a1', fontWeight: 600 }}>
                  Alertas
                </Link>
                . Los pedidos genéricos de «hablar con un humano» siguen cubiertos por una heurística global. Máximo{' '}
                {maxActive} triggers activos por agente.
              </p>
            </div>
          ) : null}

          {data ? (
            <div style={filterBar}>
              <label style={filterLabel}>
                <span>Buscar</span>
                <input
                  type="search"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Agente, frase o motivo…"
                  style={searchInput}
                />
              </label>
            </div>
          ) : null}

          {err ? (
            <div role="alert" style={errBox}>
              {err}
            </div>
          ) : null}

          {!data && !err ? (
            <div style={emptyState}>
              <p style={{ margin: 0, fontSize: 15, fontWeight: 600, color: '#475569' }}>Cargando…</p>
            </div>
          ) : !data ? null : data.agents.length === 0 ? (
            <div style={emptyState}>
              <p style={{ margin: 0, fontSize: 15, color: '#475569' }}>
                No hay agentes. Creá uno en{' '}
                <Link to="/app/agents" style={{ color: '#0369a1', fontWeight: 600 }}>
                  Agentes virtuales
                </Link>
                .
              </p>
            </div>
          ) : visibleAgents.length === 0 ? (
            <div style={emptyState}>
              <p style={{ margin: 0, fontSize: 15, color: '#475569' }}>Ningún agente coincide con la búsqueda.</p>
            </div>
          ) : (
            visibleAgents.map((agent) => {
              const open = sectionOpen[agent.id] ?? true;
              return (
                <section key={agent.id} style={agentCard}>
                  <div style={agentHeader}>
                    <button type="button" onClick={() => toggleSection(agent.id)} style={agentToggle}>
                      <span aria-hidden>{open ? '▾' : '▸'}</span>
                      <span style={{ fontWeight: 700, color: '#0f172a' }}>{agent.name}</span>
                      <span style={{ fontSize: 12, color: '#64748b', fontWeight: 500 }}>
                        {agent.activeTriggerCount} activos / {agent.triggerCount} total
                      </span>
                    </button>
                    <button type="button" onClick={() => startAdd(agent.id)} style={btnPrimarySoft}>
                      + Trigger
                    </button>
                  </div>

                  {open ? (
                    <div style={{ padding: '0 16px 16px' }}>
                      {addAgentId === agent.id ? (
                        <form onSubmit={(e) => void submitAdd(e, agent.id)} style={addForm}>
                          <TriggerFields
                            title={nTitle}
                            setTitle={setNTitle}
                            phrases={nPhrases}
                            setPhrases={setNPhrases}
                            matchMode={nMatchMode}
                            setMatchMode={setNMatchMode}
                            reason={nReason}
                            setReason={setNReason}
                            reply={nReply}
                            setReply={setNReply}
                          />
                          <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                            <button type="submit" disabled={nBusy} style={btnPri}>
                              {nBusy ? 'Guardando…' : 'Guardar'}
                            </button>
                            <button type="button" onClick={() => setAddAgentId(null)} style={btnOutline}>
                              Cancelar
                            </button>
                          </div>
                        </form>
                      ) : null}

                      {agent.triggers.length === 0 ? (
                        <p style={{ margin: '8px 0 0', fontSize: 13, color: '#94a3b8' }}>
                          Sin triggers. Agregá frases para pasar a humano (p. ej. solicitud de demo).
                        </p>
                      ) : (
                        <table style={table}>
                          <thead>
                            <tr>
                              <th style={th}>Trigger</th>
                              <th style={th}>Match</th>
                              <th style={th}>Motivo</th>
                              <th style={th}>Estado</th>
                              <th style={{ ...th, textAlign: 'right' }}>Acciones</th>
                            </tr>
                          </thead>
                          <tbody>
                            {agent.triggers.map((t, i) => (
                              <tr key={t.id} style={tableRow(i)}>
                                <td style={td}>
                                  <div style={{ fontWeight: 600, color: '#0f172a' }}>
                                    {t.title?.trim() || 'Sin título'}
                                  </div>
                                  <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>
                                    {clip(t.phrases.join(' · '), 120)}
                                  </div>
                                </td>
                                <td style={td}>{MATCH_LABELS[t.matchMode] ?? t.matchMode}</td>
                                <td style={td}>{REASON_LABELS[t.reason] ?? t.reason}</td>
                                <td style={td}>
                                  <button type="button" onClick={() => void toggleActive(agent.id, t)} style={btnSm}>
                                    {t.isActive ? 'Activo' : 'Inactivo'}
                                  </button>
                                </td>
                                <td style={{ ...td, textAlign: 'right' }}>
                                  <button type="button" onClick={() => openEdit(agent.id, t)} style={btnSm}>
                                    Editar
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => setDeleteModal({ agentId: agent.id, trigger: t })}
                                    style={{ ...btnSm, color: '#b91c1c' }}
                                  >
                                    Eliminar
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      )}
                    </div>
                  ) : null}
                </section>
              );
            })
          )}
        </div>
      </div>

      {editing
        ? createPortal(
            <div style={modalOverlay} role="presentation" onClick={() => !eBusy && setEditing(null)}>
              <div
                role="dialog"
                aria-modal
                style={modalPanel}
                onClick={(e) => e.stopPropagation()}
              >
                <h2 style={modalTitle}>Editar trigger</h2>
                <form onSubmit={(e) => void saveEdit(e)}>
                  <TriggerFields
                    title={eTitle}
                    setTitle={setETitle}
                    phrases={ePhrases}
                    setPhrases={setEPhrases}
                    matchMode={eMatchMode}
                    setMatchMode={setEMatchMode}
                    reason={eReason}
                    setReason={setEReason}
                    reply={eReply}
                    setReply={setEReply}
                  />
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 12, fontSize: 13 }}>
                    <input type="checkbox" checked={eActive} onChange={(e) => setEActive(e.target.checked)} />
                    Activo
                  </label>
                  <div style={{ display: 'flex', gap: 8, marginTop: 16, justifyContent: 'flex-end' }}>
                    <button type="button" onClick={() => setEditing(null)} disabled={eBusy} style={btnOutline}>
                      Cancelar
                    </button>
                    <button type="submit" disabled={eBusy} style={btnPri}>
                      {eBusy ? 'Guardando…' : 'Guardar'}
                    </button>
                  </div>
                </form>
              </div>
            </div>,
            document.body,
          )
        : null}

      {deleteModal
        ? createPortal(
            <div style={modalOverlay} role="presentation" onClick={() => !deleteBusy && setDeleteModal(null)}>
              <div role="dialog" aria-modal style={{ ...modalPanel, maxWidth: 420 }} onClick={(e) => e.stopPropagation()}>
                <h2 style={modalTitle}>Eliminar trigger</h2>
                <p style={{ fontSize: 14, color: '#475569', lineHeight: 1.5 }}>
                  ¿Eliminar «{deleteModal.trigger.title?.trim() || 'Sin título'}»? Esta acción no se puede deshacer.
                </p>
                <div style={{ display: 'flex', gap: 8, marginTop: 16, justifyContent: 'flex-end' }}>
                  <button type="button" onClick={() => setDeleteModal(null)} disabled={deleteBusy} style={btnOutline}>
                    Cancelar
                  </button>
                  <button
                    type="button"
                    onClick={() => void confirmDelete()}
                    disabled={deleteBusy}
                    style={{ ...btnPri, background: '#dc2626' }}
                  >
                    {deleteBusy ? 'Eliminando…' : 'Eliminar'}
                  </button>
                </div>
              </div>
            </div>,
            document.body,
          )
        : null}
    </>
  );
}

function TriggerFields(props: {
  title: string;
  setTitle: (v: string) => void;
  phrases: string;
  setPhrases: (v: string) => void;
  matchMode: 'contains' | 'exact';
  setMatchMode: (v: 'contains' | 'exact') => void;
  reason: 'explicit' | 'demo_request';
  setReason: (v: 'explicit' | 'demo_request') => void;
  reply: string;
  setReply: (v: string) => void;
}) {
  return (
    <>
      <label style={fieldLabel}>
        Título (opcional)
        <input
          value={props.title}
          onChange={(e) => props.setTitle(e.target.value.slice(0, TITLE_MAX))}
          style={input}
          placeholder="Ej. Demo wa.me"
        />
      </label>
      <label style={fieldLabel}>
        Frases (una por línea)
        <textarea
          value={props.phrases}
          onChange={(e) => props.setPhrases(e.target.value)}
          style={textarea}
          rows={4}
          placeholder={'Hola, quiero agendar una demo de Interactive Talker\nquiero agendar una demo'}
          required
        />
      </label>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <label style={fieldLabel}>
          Modo de match
          <select
            value={props.matchMode}
            onChange={(e) => props.setMatchMode(e.target.value === 'exact' ? 'exact' : 'contains')}
            style={input}
          >
            <option value="contains">Contiene</option>
            <option value="exact">Exacto</option>
          </select>
        </label>
        <label style={fieldLabel}>
          Motivo de alerta
          <select
            value={props.reason}
            onChange={(e) =>
              props.setReason(e.target.value === 'demo_request' ? 'demo_request' : 'explicit')
            }
            style={input}
          >
            <option value="demo_request">Solicitud de demo</option>
            <option value="explicit">Pedido de humano</option>
          </select>
        </label>
      </div>
      <label style={fieldLabel}>
        Respuesta al visitante (opcional)
        <textarea
          value={props.reply}
          onChange={(e) => props.setReply(e.target.value.slice(0, REPLY_MAX))}
          style={textarea}
          rows={3}
          placeholder="Vacío = mensaje por defecto del motivo"
        />
        <span style={charHint}>{props.reply.length}/{REPLY_MAX}</span>
      </label>
    </>
  );
}

function tableRow(rowIndex: number): CSSProperties {
  return {
    borderTop: '1px solid #e2e8f0',
    background: rowIndex % 2 === 0 ? '#ffffff' : '#f8fafc',
  };
}

const pageShell: CSSProperties = { minHeight: '100%', background: '#f1f5f9' };
const pageScroll: CSSProperties = { maxWidth: 1100, margin: '0 auto', padding: '28px 24px 48px' };
const pageHeaderRow: CSSProperties = {
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'space-between',
  gap: 16,
  marginBottom: 16,
};
const pageTitle: CSSProperties = {
  margin: 0,
  fontSize: 22,
  fontWeight: 800,
  letterSpacing: '-0.02em',
  color: '#0f172a',
};
const pageSubtitle: CSSProperties = { margin: '6px 0 0', fontSize: 14, color: '#64748b' };
const helpBox: CSSProperties = {
  marginBottom: 16,
  padding: '14px 16px',
  borderRadius: 12,
  background: '#fff',
  border: '1px solid #e2e8f0',
};
const filterBar: CSSProperties = { marginBottom: 16 };
const filterLabel: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 6,
  fontSize: 12,
  fontWeight: 600,
  color: '#64748b',
  maxWidth: 360,
};
const searchInput: CSSProperties = {
  padding: '8px 12px',
  borderRadius: 10,
  border: '1px solid #e2e8f0',
  fontSize: 14,
};
const errBox: CSSProperties = {
  marginBottom: 12,
  padding: '10px 14px',
  borderRadius: 10,
  background: '#fef2f2',
  color: '#b91c1c',
  fontSize: 14,
};
const emptyState: CSSProperties = {
  padding: 32,
  textAlign: 'center',
  background: '#fff',
  borderRadius: 12,
  border: '1px solid #e2e8f0',
};
const agentCard: CSSProperties = {
  marginBottom: 14,
  background: '#fff',
  borderRadius: 14,
  border: '1px solid #e2e8f0',
  overflow: 'hidden',
};
const agentHeader: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 12,
  padding: '12px 16px',
};
const agentToggle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  border: 'none',
  background: 'transparent',
  cursor: 'pointer',
  padding: 0,
  fontSize: 14,
};
const addForm: CSSProperties = {
  marginBottom: 14,
  padding: 14,
  borderRadius: 12,
  background: '#f8fafc',
  border: '1px solid #e2e8f0',
};
const table: CSSProperties = { width: '100%', borderCollapse: 'collapse', fontSize: 13 };
const th: CSSProperties = {
  textAlign: 'left',
  padding: '8px 10px',
  fontSize: 11,
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
  color: '#94a3b8',
  fontWeight: 700,
};
const td: CSSProperties = { padding: '10px', verticalAlign: 'top' };
const fieldLabel: CSSProperties = {
  display: 'block',
  marginBottom: 10,
  fontSize: 13,
  fontWeight: 600,
  color: '#334155',
};
const input: CSSProperties = {
  display: 'block',
  width: '100%',
  marginTop: 6,
  padding: '8px 10px',
  borderRadius: 8,
  border: '1px solid #e2e8f0',
  fontSize: 13,
  boxSizing: 'border-box',
};
const textarea: CSSProperties = { ...input, resize: 'vertical', fontFamily: 'inherit' };
const charHint: CSSProperties = { display: 'block', marginTop: 4, fontSize: 11, color: '#94a3b8' };
const btnGhost: CSSProperties = {
  padding: '8px 14px',
  borderRadius: 10,
  border: '1px solid #e2e8f0',
  background: '#fff',
  color: '#475569',
  fontWeight: 600,
  fontSize: 13,
  cursor: 'pointer',
};
const btnPri: CSSProperties = {
  padding: '10px 18px',
  borderRadius: 10,
  border: 'none',
  background: '#0ea5e9',
  color: '#fff',
  fontWeight: 600,
  cursor: 'pointer',
  fontSize: 14,
};
const btnOutline: CSSProperties = {
  padding: '9px 14px',
  borderRadius: 10,
  border: '1px solid #cbd5e1',
  background: '#fff',
  color: '#334155',
  fontWeight: 600,
  cursor: 'pointer',
  fontSize: 13,
};
const btnPrimarySoft: CSSProperties = {
  ...btnOutline,
  background: '#e0f2fe',
  borderColor: '#7dd3fc',
  color: '#0369a1',
};
const btnSm: CSSProperties = {
  padding: '6px 10px',
  marginRight: 6,
  fontSize: 12,
  fontWeight: 600,
  borderRadius: 8,
  border: '1px solid #e2e8f0',
  background: '#f8fafc',
  color: '#334155',
  cursor: 'pointer',
};
const modalOverlay: CSSProperties = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(15, 23, 42, 0.55)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 16,
  zIndex: 100,
};
const modalPanel: CSSProperties = {
  background: '#fff',
  borderRadius: 16,
  padding: 24,
  maxWidth: 560,
  width: '100%',
  maxHeight: 'min(90vh, 720px)',
  overflow: 'auto',
  boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.28)',
  border: '1px solid #e2e8f0',
};
const modalTitle: CSSProperties = {
  marginTop: 0,
  marginBottom: 16,
  paddingBottom: 12,
  borderBottom: '1px solid #f1f5f9',
  fontSize: 18,
  fontWeight: 700,
};
